/**
 * Validacion del formulario publico.
 *
 * La cedula y el telefono **no se validan aca**: se delega en `normalizarCedula` y
 * `normalizarTelefono` de `packages/domain`, que son las mismas funciones que usa el
 * servidor. Reescribir la expresion regular del digito verificador en el navegador seria
 * exactamente la duplicacion que ADR-003 existe para evitar: el dia que cambie la regla,
 * cambiaria en un solo lado.
 *
 * La validacion del navegador es una **cortesia**, no un control. El servidor vuelve a
 * validar todo y su respuesta manda (docs/SEGURIDAD.md §2).
 *
 * ## Condiciones de cancelación
 *
 * `docs/API.md` define un unico campo `consentimiento` en la callable. La pantalla lo usa
 * exclusivamente para la aceptación de las condiciones de cancelación. El flujo público
 * no solicita autorización para mensajes automáticos de WhatsApp (ADR-067).
 */

import { z } from 'zod';

import { normalizarCedula, normalizarTelefono } from '@padel/domain';

/** Topes de longitud. Los mismos que `functions/src/esquemas/entradas.ts`. */
export const LARGOS = {
  cedula: 20,
  nombre: 60,
  apellido: 60,
  telefono: 25,
} as const;

const nombrePropio = (maximo: number, vacio: string, largo: string): z.ZodString =>
  z.string().trim().min(1, vacio).max(maximo, largo);

export const esquemaFormularioReserva = z
  .object({
    cedula: z
      .string()
      .trim()
      .min(1, 'Ingresa tu cedula.')
      .max(LARGOS.cedula, 'La cedula es demasiado larga.')
      .refine(
        (valor) => normalizarCedula(valor).ok,
        'La cedula no es valida. Revisa el numero, incluido el digito verificador.',
      ),

    nombre: z.string().trim().max(LARGOS.nombre, 'El nombre es demasiado largo.'),

    apellido: z.string().trim().max(LARGOS.apellido, 'El apellido es demasiado largo.'),

    telefono: z.string().trim().max(LARGOS.telefono, 'El telefono es demasiado largo.'),

    usarDatosRegistrados: z.boolean(),

    aceptaCancelacion: z
      .boolean()
      .refine((valor) => valor, 'Tenes que aceptar las condiciones de cancelacion.'),
  })
  .superRefine((datos, contexto) => {
    if (datos.usarDatosRegistrados) return;

    const campos = [
      {
        campo: 'nombre' as const,
        esquema: nombrePropio(LARGOS.nombre, 'Ingresa tu nombre.', 'El nombre es demasiado largo.'),
      },
      {
        campo: 'apellido' as const,
        esquema: nombrePropio(
          LARGOS.apellido,
          'Ingresa tu apellido.',
          'El apellido es demasiado largo.',
        ),
      },
    ];

    for (const { campo, esquema } of campos) {
      const resultado = esquema.safeParse(datos[campo]);
      if (!resultado.success) {
        contexto.addIssue({
          code: 'custom',
          path: [campo],
          message: resultado.error.issues[0]?.message ?? 'Dato invalido.',
        });
      }
    }

    if (datos.telefono.length === 0) {
      contexto.addIssue({ code: 'custom', path: ['telefono'], message: 'Ingresa tu telefono.' });
    } else if (!normalizarTelefono(datos.telefono).ok) {
      contexto.addIssue({
        code: 'custom',
        path: ['telefono'],
        message: 'El telefono no es valido. Ejemplo: 099 000 000.',
      });
    }
  });

/** Valores del formulario, ya validados. */
export type FormularioReserva = z.infer<typeof esquemaFormularioReserva>;

/** Estado inicial. Todos los campos vacios y las condiciones sin aceptar. */
export const FORMULARIO_VACIO: FormularioReserva = {
  cedula: '',
  nombre: '',
  apellido: '',
  telefono: '',
  usarDatosRegistrados: false,
  aceptaCancelacion: false,
};
